import { useState, useRef, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

const ROTATION_INTERVAL_MS = 6000;

export default function useRotatingContent(options) {
  const [shownIdx, setShownIdx] = useState(
    options.rotatingContentStartIdx ?? null
  );
  const [isContentShown, setIsContentShown] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [ref, inView] = useInView({ threshold: 0.5 });
  const prevShownIdxRef = useRef();

  function updateShownIdxIfNeeded(idx) {
    if (idx !== shownIdx) {
      setShownIdx(idx);
      setIsContentShown(false);
    }
  }

  function pause(idx = shownIdx) {
    updateShownIdxIfNeeded(idx);
    setIsPaused(true);
  }

  function resume() {
    setIsPaused(false);
  }

  function increment() {
    updateShownIdxIfNeeded((shownIdx + 1) % options.rotatingContentCount);
  }

  function next() {
    increment();
  }

  function prev() {
    updateShownIdxIfNeeded(Math.max(shownIdx - 1, 0));
  }

  useEffect(() => {
    if (ref && inView) {
      setShownIdx(prevShownIdx => prevShownIdx || 0);
    }
  }, [ref, inView]);

  useEffect(() => {
    prevShownIdxRef.current = shownIdx;
  }, [shownIdx]);

  useEffect(() => {
    let intervalId;
    // ensure not paused and element is in view before setting interval
    if (!isPaused && inView) {
      intervalId = setInterval(increment, ROTATION_INTERVAL_MS);
    }
    return () => clearInterval(intervalId);
  }, [shownIdx, isPaused, inView]);

  useEffect(() => {
    if (!isContentShown) {
      // HACK: ensure we allow time for repaint when `false` so fade-in
      // animation displays correctly when setting to `true`
      window.setTimeout(() => {
        setIsContentShown(true);
      }, 100);
    }
  }, [isContentShown]);

  return {
    ref,
    rotatingContentInterval: ROTATION_INTERVAL_MS,
    isRotatingContentShown: isContentShown,
    shownRotatingContentIdx: shownIdx,
    isRotatingContentPaused: isPaused,
    pauseRotatingContent: pause,
    resumeRotatingContent: resume,
    nextRotatingContent: next,
    prevRotatingContent: prev,
    isOnFirstRotatingContentStep: shownIdx === 0,
    isOnLastRotatingContentStep: shownIdx === options.rotatingContentCount - 1
  };
}
